import React, { useState, useEffect } from 'react';
import { useAuthorDashboard } from '../AuthorDashboardContext';
import { db, functions } from '../../firebase';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, where, orderBy
} from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Package, Plus, Save, Pencil, Trash2, ChevronDown, ChevronUp, BookOpen,
  ShoppingCart, Search, Clock, CreditCard, CheckCircle, Truck, XCircle,
  MapPin, User, Mail, Calendar, DollarSign, Eye, EyeOff, Archive, ArchiveRestore,
  FileText, Headphones, Phone, Tag, Percent, Copy, Check, BookText, AlertCircle
} from 'lucide-react';

const UK_COUNTRIES = ['uk', 'gb', 'united kingdom', 'england', 'scotland', 'wales', 'northern ireland', 'great britain'];
const isUK = (country) => country && UK_COUNTRIES.includes(country.trim().toLowerCase());

// ── Product types ──
const PRODUCT_TYPES = [
  'Paperback Signed',
  'Hardback Signed',
  'Paperback Special Edition Box',
  'Hardback Special Edition Box',
  'Custom',
];

// ── Order statuses ──
const ORDER_STATUSES = [
  { key: 'Paid', label: 'Paid', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  { key: 'Shipped', label: 'Shipped', color: 'bg-blue-100 text-blue-800', icon: Truck },
  { key: 'Delivered', label: 'Delivered', color: 'bg-teal-100 text-teal-800', icon: Package },
  { key: 'Completed', label: 'Completed', color: 'bg-slate-100 text-slate-800', icon: CheckCircle },
  { key: 'Cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle },
];

// Pre-order status (kept separate, only shown for pre-order type orders)
const PRE_ORDER_STATUS = { key: 'Pre-Order Pending', label: 'Pre-Order', color: 'bg-indigo-100 text-indigo-800', icon: Clock };

// Legacy status mapping
const normalizeStatus = (s) => {
  const map = {
    pending: 'Paid', payment: 'Paid', paid: 'Paid',
    processing: 'Paid', shipped: 'Shipped', delivered: 'Delivered',
    cancelled: 'Cancelled', 'pre-order': 'Pre-Order Pending',
    Pending: 'Paid', Processing: 'Paid',
  };
  return map[s] || s || 'Paid';
};

export default function AuthorOrdersTab() {
  const { books, basePath, authorId, ebooks, audiobooks, readers } = useAuthorDashboard();

  // ── Product state ──
  const [selectedBookId, setSelectedBookId] = useState('');
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProduct, setNewProduct] = useState({
    name: '', description: '', price: '', productType: 'Paperback Signed', available: true,
    shippingUK: '3.50', shippingEurope: '8.00', shippingUSCanada: '12.00', shippingRestOfWorld: '14.00',
    availability: 'in_stock', stockQuantity: '', bookId: '',
  });
  const [savingProduct, setSavingProduct] = useState(false);

  // ── eBook/Audiobook edit state ──
  const [editingEbook, setEditingEbook] = useState(null);
  const [editingAudiobook, setEditingAudiobook] = useState(null);
  const [savingDigital, setSavingDigital] = useState(false);
  const [collapsedPreOrderType, setCollapsedPreOrderType] = useState({ ebook: true, audiobook: true });

  // ── Order state ──
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [orderBookFilter, setOrderBookFilter] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderSearch, setOrderSearch] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [collapsedBooks, setCollapsedBooks] = useState({});
  const [collapsedProducts, setCollapsedProducts] = useState({});
  const [collapsedRegions, setCollapsedRegions] = useState({});

  // ── Promo code state ──
  const [promoCodes, setPromoCodes] = useState([]);
  const [loadingPromos, setLoadingPromos] = useState(true);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [newPromo, setNewPromo] = useState({ code: '', discountType: 'percentage', discountMode: 'all', discountValue: '', active: true, productOverrides: {}, perProductDiscounts: { ebook: '', audiobook: '', physical: '' }, expiresAt: '', maxUses: '' });
  const [savingPromo, setSavingPromo] = useState(false);
  const [copiedPromoId, setCopiedPromoId] = useState(null);
  const [allProducts, setAllProducts] = useState([]); // all products across all books, for promo overrides

  // ── Auto-select first book ──
  useEffect(() => {
    if (books.length > 0 && !selectedBookId) {
      const active = books.find(b => b.status !== 'archived');
      setSelectedBookId(active?.id || books[0].id);
    }
  }, [books, selectedBookId]);

  // ── Listen to products for selected book ──
  useEffect(() => {
    if (!basePath || !selectedBookId) { setProducts([]); return; }
    setLoadingProducts(true);
    const unsub = onSnapshot(
      collection(db, basePath, 'books', selectedBookId, 'products'),
      (snap) => {
        const items = [];
        snap.forEach(d => items.push({ id: d.id, ...d.data() }));
        setProducts(items);
        setLoadingProducts(false);
      },
      (err) => { console.error('Products listener error:', err); setLoadingProducts(false); }
    );
    return unsub;
  }, [basePath, selectedBookId]);

  // ── Listen to all orders for this author ──
  useEffect(() => {
    if (!basePath) return;
    setLoadingOrders(true);
    const unsub = onSnapshot(
      collection(db, basePath, 'orders'),
      (snap) => {
        const items = [];
        snap.forEach(d => items.push({ id: d.id, ...d.data() }));
        items.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setOrders(items);
        setLoadingOrders(false);
      },
      (err) => { console.error('Orders listener error:', err); setLoadingOrders(false); }
    );
    return unsub;
  }, [basePath]);

  // ── Listen to promo codes ──
  useEffect(() => {
    if (!basePath) return;
    setLoadingPromos(true);
    const unsub = onSnapshot(
      collection(db, basePath, 'promoCodes'),
      (snap) => {
        const items = [];
        snap.forEach(d => items.push({ id: d.id, ...d.data() }));
        items.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
        setPromoCodes(items);
        setLoadingPromos(false);
      },
      (err) => { console.error('Promo codes listener error:', err); setLoadingPromos(false); }
    );
    return unsub;
  }, [basePath]);

  // ── Load all products across all books (for promo override editor) ──
  useEffect(() => {
    if (!basePath || !books.length) { setAllProducts([]); return; }
    const unsubs = [];
    const productsByBook = {};
    books.forEach(book => {
      const unsub = onSnapshot(
        collection(db, basePath, 'books', book.id, 'products'),
        (snap) => {
          const items = [];
          snap.forEach(d => items.push({ id: d.id, bookId: book.id, bookTitle: book.title, ...d.data() }));
          productsByBook[book.id] = items;
          setAllProducts(Object.values(productsByBook).flat());
        }
      );
      unsubs.push(unsub);
    });
    return () => unsubs.forEach(u => u());
  }, [basePath, books]);

  // ── Product CRUD ──
  const addProduct = async () => {
    const targetBookId = newProduct.bookId || selectedBookId;
    if (!newProduct.name || !newProduct.price || !targetBookId) return;
    setSavingProduct(true);
    try {
      const stockVal = newProduct.stockQuantity === '' ? null : parseInt(newProduct.stockQuantity, 10);
      const availability = stockVal === 0 ? 'sold_out' : (newProduct.availability === 'sold_out' ? 'sold_out' : (newProduct.availability || 'in_stock'));
      const finalStock = newProduct.availability === 'sold_out' ? 0 : stockVal;
      await addDoc(collection(db, basePath, 'books', targetBookId, 'products'), {
        name: newProduct.name.trim(),
        description: newProduct.description.trim(),
        price: parseFloat(newProduct.price) || 0,
        productType: newProduct.productType,
        available: newProduct.available,
        availability,
        stockQuantity: finalStock,
        shippingUK: parseFloat(newProduct.shippingUK) || 3.50,
        shippingEurope: parseFloat(newProduct.shippingEurope) || 8.00,
        shippingUSCanada: parseFloat(newProduct.shippingUSCanada) || 12.00,
        shippingRestOfWorld: parseFloat(newProduct.shippingRestOfWorld) || 14.00,
        bookId: targetBookId,
        authorId,
        guestAccess: newProduct.guestAccess ?? false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      setNewProduct({ name: '', description: '', price: '', productType: 'Paperback Signed', available: true, shippingUK: '3.50', shippingEurope: '8.00', shippingUSCanada: '12.00', shippingRestOfWorld: '14.00', availability: 'in_stock', stockQuantity: '', bookId: '', guestAccess: false });
      setShowAddForm(false);
    } catch (err) {
      alert('Error adding product: ' + err.message);
    } finally {
      setSavingProduct(false);
    }
  };

  const saveEditProduct = async () => {
    if (!editingProduct) return;
    setSavingProduct(true);
    try {
      const editStockVal = editingProduct.stockQuantity === '' || editingProduct.stockQuantity === null || editingProduct.stockQuantity === undefined ? null : parseInt(editingProduct.stockQuantity, 10);
      const editAvailability = editStockVal === 0 ? 'sold_out' : (editingProduct.availability === 'sold_out' ? 'sold_out' : (editingProduct.availability || 'in_stock'));
      const editFinalStock = editingProduct.availability === 'sold_out' ? 0 : editStockVal;
      const targetBookId = editingProduct.bookId || selectedBookId;
      const productData = {
        name: editingProduct.name.trim(),
        description: editingProduct.description.trim(),
        price: parseFloat(editingProduct.price) || 0,
        productType: editingProduct.productType,
        available: editingProduct.available,
        availability: editAvailability,
        stockQuantity: editFinalStock,
        shippingUK: parseFloat(editingProduct.shippingUK) || 3.50,
        shippingEurope: parseFloat(editingProduct.shippingEurope) || 8.00,
        shippingUSCanada: parseFloat(editingProduct.shippingUSCanada) || 12.00,
        shippingRestOfWorld: parseFloat(editingProduct.shippingRestOfWorld) || 14.00,
        guestAccess: editingProduct.guestAccess ?? false,
        bookId: targetBookId,
        updatedAt: new Date().toISOString(),
      };

      if (targetBookId !== selectedBookId) {
        // Book changed — move product: create in new book, delete from old
        await addDoc(collection(db, basePath, 'books', targetBookId, 'products'), {
          ...productData,
          authorId,
          createdAt: editingProduct.createdAt || new Date().toISOString(),
        });
        await deleteDoc(doc(db, basePath, 'books', selectedBookId, 'products', editingProduct.id));
      } else {
        await updateDoc(doc(db, basePath, 'books', selectedBookId, 'products', editingProduct.id), productData);
      }
      setEditingProduct(null);
    } catch (err) {
      alert('Error updating product: ' + err.message);
    } finally {
      setSavingProduct(false);
    }
  };

  const toggleAvailability = async (product) => {
    try {
      await updateDoc(doc(db, basePath, 'books', selectedBookId, 'products', product.id), {
        available: !product.available,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      alert('Error toggling availability: ' + err.message);
    }
  };

  const deleteProduct = async (product) => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, basePath, 'books', selectedBookId, 'products', product.id));
    } catch (err) {
      alert('Error deleting product: ' + err.message);
    }
  };

  // ── Digital product save ──
  const saveDigitalProduct = async (type, item) => {
    setSavingDigital(true);
    try {
      const collName = type === 'ebook' ? 'ebooks' : 'audiobooks';
      await updateDoc(doc(db, basePath, collName, item.id), {
        price: parseFloat(item.price) || 0,
        status: item.status || 'draft',
        visibleToGuests: item.visibleToGuests ?? true,
        availableToGuests: item.availableToGuests ?? false,
        updatedAt: new Date().toISOString(),
      });
      if (type === 'ebook') setEditingEbook(null);
      else setEditingAudiobook(null);
    } catch (err) {
      alert('Error saving: ' + err.message);
    } finally {
      setSavingDigital(false);
    }
  };

  // ── Order actions ──
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await updateDoc(doc(db, basePath, 'orders', orderId), {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      alert('Error updating order: ' + err.message);
    }
  };

  // ── Tracking info ──
  const [trackingEdits, setTrackingEdits] = useState({}); // { [orderId]: { trackingNumber, deliveryProvider } }
  const [trackingSaving, setTrackingSaving] = useState({});

  const getTrackingEdit = (order) => trackingEdits[order.id] || {
    trackingNumber: order.trackingNumber || '',
    deliveryProvider: order.deliveryProvider || '',
  };

  const setTrackingField = (orderId, field, value) => {
    setTrackingEdits(prev => ({
      ...prev,
      [orderId]: { ...getTrackingEdit({ id: orderId, ...trackingEdits[orderId] }), [field]: value },
    }));
  };

  const saveTracking = async (order) => {
    const edit = getTrackingEdit(order);
    setTrackingSaving(prev => ({ ...prev, [order.id]: true }));
    try {
      await updateDoc(doc(db, basePath, 'orders', order.id), {
        trackingNumber: edit.trackingNumber.trim(),
        deliveryProvider: edit.deliveryProvider.trim(),
        updatedAt: new Date().toISOString(),
      });
      setTrackingEdits(prev => { const n = { ...prev }; delete n[order.id]; return n; });
    } catch (err) {
      alert('Error saving tracking info: ' + err.message);
    } finally {
      setTrackingSaving(prev => ({ ...prev, [order.id]: false }));
    }
  };

  // ── Promo code CRUD ──
  const defaultPromo = { code: '', discountType: 'percentage', discountMode: 'all', discountValue: '', active: true, productOverrides: {}, perProductDiscounts: { ebook: '', audiobook: '', physical: '' }, expiresAt: '', maxUses: '' };

  const addPromoCode = async () => {
    if (!newPromo.code) return;
    const isFS = newPromo.discountType === 'free_shipping';
    if (!isFS && newPromo.discountMode === 'all' && !newPromo.discountValue) return;
    if (!isFS && newPromo.discountMode === 'per_product') {
      const ppd = newPromo.perProductDiscounts || {};
      const hasOverrides = Object.keys(newPromo.productOverrides || {}).length > 0;
      const hasCategoryDiscounts = Object.values(ppd).some(v => v !== '' && v !== undefined);
      if (!hasCategoryDiscounts && !hasOverrides) return;
    }
    setSavingPromo(true);
    try {
      const promoData = {
        code: newPromo.code.trim().toUpperCase(),
        discountType: newPromo.discountType || 'percentage',
        discountMode: newPromo.discountMode || 'all',
        active: newPromo.active,
        usageCount: 0,
        authorId,
        createdAt: new Date().toISOString(),
        expiresAt: newPromo.expiresAt || null,
        maxUses: newPromo.maxUses ? parseInt(newPromo.maxUses, 10) : null,
      };
      if (isFS) {
        promoData.discountValue = 0;
        promoData.freeShipping = true;
        promoData.discountMode = 'all';
        promoData.perProductDiscounts = {};
        promoData.productOverrides = {};
      } else if (newPromo.discountMode === 'all') {
        promoData.discountValue = parseFloat(newPromo.discountValue) || 0;
        promoData.freeShipping = false;
        promoData.perProductDiscounts = {};
        promoData.productOverrides = {};
      } else {
        promoData.discountValue = 0;
        // Category-level discounts (ebook / audiobook / physical)
        const ppd = {};
        if (newPromo.perProductDiscounts?.ebook !== '' && newPromo.perProductDiscounts?.ebook !== undefined) ppd.ebook = parseFloat(newPromo.perProductDiscounts.ebook) || 0;
        if (newPromo.perProductDiscounts?.audiobook !== '' && newPromo.perProductDiscounts?.audiobook !== undefined) ppd.audiobook = parseFloat(newPromo.perProductDiscounts.audiobook) || 0;
        if (newPromo.perProductDiscounts?.physical !== '' && newPromo.perProductDiscounts?.physical !== undefined) ppd.physical = parseFloat(newPromo.perProductDiscounts.physical) || 0;
        promoData.perProductDiscounts = ppd;
        // Legacy per-product-ID overrides
        promoData.productOverrides = newPromo.productOverrides || {};
      }
      await addDoc(collection(db, basePath, 'promoCodes'), promoData);
      setNewPromo({ ...defaultPromo });
      setShowPromoForm(false);
    } catch (err) {
      alert('Error creating promo code: ' + err.message);
    } finally {
      setSavingPromo(false);
    }
  };

  const saveEditPromo = async () => {
    if (!editingPromo) return;
    setSavingPromo(true);
    try {
      const promoData = {
        code: editingPromo.code.trim().toUpperCase(),
        discountType: editingPromo.discountType || 'percentage',
        discountMode: editingPromo.discountMode || 'all',
        active: editingPromo.active,
        updatedAt: new Date().toISOString(),
        expiresAt: editingPromo.expiresAt || null,
        maxUses: editingPromo.maxUses ? parseInt(editingPromo.maxUses, 10) : null,
      };
      if (editingPromo.discountType === 'free_shipping') {
        promoData.discountValue = 0;
        promoData.freeShipping = true;
        promoData.discountMode = 'all';
        promoData.perProductDiscounts = {};
        promoData.productOverrides = {};
      } else if ((editingPromo.discountMode || 'all') === 'all') {
        promoData.discountValue = parseFloat(editingPromo.discountValue) || 0;
        promoData.freeShipping = false;
      } else {
        promoData.discountValue = 0;
        const ppd = {};
        if (editingPromo.perProductDiscounts?.ebook !== '' && editingPromo.perProductDiscounts?.ebook !== undefined) ppd.ebook = parseFloat(editingPromo.perProductDiscounts.ebook) || 0;
        if (editingPromo.perProductDiscounts?.audiobook !== '' && editingPromo.perProductDiscounts?.audiobook !== undefined) ppd.audiobook = parseFloat(editingPromo.perProductDiscounts.audiobook) || 0;
        if (editingPromo.perProductDiscounts?.physical !== '' && editingPromo.perProductDiscounts?.physical !== undefined) ppd.physical = parseFloat(editingPromo.perProductDiscounts.physical) || 0;
        promoData.perProductDiscounts = ppd;
        promoData.productOverrides = editingPromo.productOverrides || {};
      }
      await updateDoc(doc(db, basePath, 'promoCodes', editingPromo.id), promoData);
      setEditingPromo(null);
    } catch (err) {
      alert('Error updating promo code: ' + err.message);
    } finally {
      setSavingPromo(false);
    }
  };

  const deletePromoCode = async (promo) => {
    if (!window.confirm(`Delete promo code "${promo.code}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, basePath, 'promoCodes', promo.id));
    } catch (err) {
      alert('Error deleting promo code: ' + err.message);
    }
  };

  const togglePromoActive = async (promo) => {
    try {
      await updateDoc(doc(db, basePath, 'promoCodes', promo.id), {
        active: !promo.active,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      alert('Error toggling promo code: ' + err.message);
    }
  };

  const copyPromoCode = (promo) => {
    navigator.clipboard.writeText(promo.code);
    setCopiedPromoId(promo.id);
    setTimeout(() => setCopiedPromoId(null), 2000);
  };

  // ── Filtered orders ──
  const filteredOrders = orders.filter(o => {
    const matchBook = orderBookFilter === 'all' || o.bookId === orderBookFilter;
    const displayStatus = normalizeStatus(o.status);
    const matchStatus = orderStatusFilter === 'all' || displayStatus === orderStatusFilter;
    const matchSearch = !orderSearch ||
      o.readerName?.toLowerCase().includes(orderSearch.toLowerCase()) ||
      o.readerEmail?.toLowerCase().includes(orderSearch.toLowerCase()) ||
      o.productName?.toLowerCase().includes(orderSearch.toLowerCase());
    return matchBook && matchStatus && matchSearch;
  });

  // ── Split pre-orders from regular orders ──
  const regularOrders = filteredOrders.filter(o => o.type !== 'pre-order');
  const preOrders = filteredOrders.filter(o => o.type === 'pre-order');

  // ── Stats ──
  const stats = {
    total: orders.filter(o => o.type !== 'pre-order').length,
    paid: orders.filter(o => o.type !== 'pre-order' && normalizeStatus(o.status) === 'Paid').length,
    shipped: orders.filter(o => o.type !== 'pre-order' && normalizeStatus(o.status) === 'Shipped').length,
    revenue: orders
      .filter(o => o.type !== 'pre-order' && ['Paid', 'Shipped', 'Delivered', 'Completed'].includes(normalizeStatus(o.status)))
      .reduce((s, o) => s + (o.amount || o.total || 0), 0),
    preOrders: orders.filter(o => o.type === 'pre-order').length,
  };

  const activeBooks = books
    .filter(b => b.status !== 'archived')
    .sort((a, b) => {
      // Book 1 first, then alphabetical
      if (a.id === 'book1') return -1;
      if (b.id === 'book1') return 1;
      return (a.title || '').localeCompare(b.title || '');
    });

  // Default book filter to first book (Book 1)
  useEffect(() => {
    if (!orderBookFilter && activeBooks.length > 0) {
      setOrderBookFilter(activeBooks[0].id);
    }
  }, [activeBooks, orderBookFilter]);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Orders & Products</h1>
        <p className="mt-1 text-sm text-slate-500">Manage your per-book products and track reader orders</p>
      </div>

      {/* ═══════════════════════════════════════
          SECTION A: PRODUCT MANAGEMENT
          ═══════════════════════════════════════ */}
      <div className="space-y-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Product Management</h2>
            <Button onClick={() => { setShowAddForm(!showAddForm); if (!showAddForm) setNewProduct(p => ({ ...p, bookId: selectedBookId })); }} size="sm">
              <Plus className="mr-1 h-4 w-4" />
              Add Product
            </Button>
          </div>
          {/* Book selector buttons */}
          <div className="flex flex-wrap gap-2">
            {activeBooks.map(b => (
              <button
                key={b.id}
                onClick={() => setSelectedBookId(b.id)}
                className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                  selectedBookId === b.id
                    ? 'border-teal-300 bg-teal-50 text-teal-800'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                {b.title}
              </button>
            ))}
          </div>
        </div>

        {/* Add product form */}
        {showAddForm && (
          <Card className="border-teal-200 bg-teal-50/30">
            <CardContent className="p-5 space-y-4">
              <h3 className="font-semibold text-slate-900">New Product</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Product Name *</Label>
                  <Input
                    value={newProduct.name}
                    onChange={e => setNewProduct(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Paperback Signed"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Product Type *</Label>
                  <select
                    value={newProduct.productType}
                    onChange={e => setNewProduct(p => ({ ...p, productType: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                  >
                    {PRODUCT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Book *</Label>
                  <select
                    value={newProduct.bookId || selectedBookId}
                    onChange={e => setNewProduct(p => ({ ...p, bookId: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                  >
                    {books.map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <Label>Description</Label>
                  <Input
                    value={newProduct.description}
                    onChange={e => setNewProduct(p => ({ ...p, description: e.target.value }))}
                    placeholder="Brief description"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Price (£) *</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={newProduct.price}
                    onChange={e => setNewProduct(p => ({ ...p, price: e.target.value }))}
                    placeholder="25.00"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Stock Quantity</Label>
                  <Input
                    type="number" min="0" step="1"
                    value={newProduct.stockQuantity}
                    onChange={e => {
                      const val = e.target.value;
                      setNewProduct(p => ({
                        ...p,
                        stockQuantity: val,
                        ...(val !== '' && parseInt(val, 10) === 0 ? { availability: 'sold_out' } : {}),
                      }));
                    }}
                    placeholder="Leave blank to not track"
                    className="mt-1"
                  />
                  <p className="mt-1 text-xs text-slate-400">Leave blank to not track stock.</p>
                </div>
                <div className="sm:col-span-2">
                  <Label>Shipping Rates</Label>
                  <div className="mt-1 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div>
                      <Label className="text-xs text-slate-500">UK (£)</Label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={newProduct.shippingUK}
                        onChange={e => setNewProduct(p => ({ ...p, shippingUK: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Europe (£)</Label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={newProduct.shippingEurope}
                        onChange={e => setNewProduct(p => ({ ...p, shippingEurope: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">USA/Canada (£)</Label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={newProduct.shippingUSCanada}
                        onChange={e => setNewProduct(p => ({ ...p, shippingUSCanada: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Rest of World (£)</Label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={newProduct.shippingRestOfWorld}
                        onChange={e => setNewProduct(p => ({ ...p, shippingRestOfWorld: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Availability</Label>
                  <select
                    value={newProduct.availability}
                    onChange={e => {
                      const val = e.target.value;
                      setNewProduct(p => ({
                        ...p,
                        availability: val,
                        ...(val === 'sold_out' ? { stockQuantity: '0' } : {}),
                      }));
                    }}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                  >
                    <option value="in_stock">In Stock</option>
                    <option value="available_soon">Available Soon</option>
                    <option value="sold_out">Sold Out</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newProduct.available}
                    onChange={e => setNewProduct(p => ({ ...p, available: e.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300 text-teal-600"
                  />
                  <Label>Available for purchase</Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newProduct.guestAccess ?? false}
                    onChange={e => setNewProduct(p => ({ ...p, guestAccess: e.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300 text-teal-600"
                  />
                  <Label>Guest access <span className="text-xs text-slate-400 font-normal">(overrides default)</span></Label>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={addProduct} disabled={savingProduct || !newProduct.name || !newProduct.price}>
                  <Save className="mr-1 h-4 w-4" />
                  {savingProduct ? 'Saving...' : 'Save Product'}
                </Button>
                <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Product list */}
        {loadingProducts ? (
          <div className="py-8 text-center text-sm text-slate-400">Loading products...</div>
        ) : products.length === 0 ? (
          <Card className="p-8 text-center">
            <Package className="mx-auto mb-2 h-10 w-10 text-slate-300" />
            <p className="text-slate-500">No products for this book yet. Add one above.</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {products.map(product => (
              <Card key={product.id} className="p-4">
                {editingProduct?.id === product.id ? (
                  /* Inline edit form */
                  <div className="space-y-3">
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <div>
                        <Label className="text-xs">Name</Label>
                        <Input
                          value={editingProduct.name}
                          onChange={e => setEditingProduct(p => ({ ...p, name: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Type</Label>
                        <select
                          value={editingProduct.productType}
                          onChange={e => setEditingProduct(p => ({ ...p, productType: e.target.value }))}
                          className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                        >
                          {PRODUCT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Book</Label>
                        <select
                          value={editingProduct.bookId || selectedBookId}
                          onChange={e => setEditingProduct(p => ({ ...p, bookId: e.target.value }))}
                          className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                        >
                          {books.map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Price (£)</Label>
                        <Input
                          type="number" step="0.01" min="0"
                          value={editingProduct.price}
                          onChange={e => setEditingProduct(p => ({ ...p, price: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <Label className="text-xs">Description</Label>
                        <Input
                          value={editingProduct.description || ''}
                          onChange={e => setEditingProduct(p => ({ ...p, description: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Stock Quantity</Label>
                        <Input
                          type="number" min="0" step="1"
                          value={editingProduct.stockQuantity ?? ''}
                          onChange={e => {
                            const val = e.target.value;
                            setEditingProduct(p => ({
                              ...p,
                              stockQuantity: val === '' ? null : val,
                              ...(val !== '' && parseInt(val, 10) === 0 ? { availability: 'sold_out' } : {}),
                            }));
                          }}
                          placeholder="Leave blank to not track"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Shipping Rates</Label>
                      <div className="mt-1 grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <div>
                          <Label className="text-xs text-slate-500">UK (£)</Label>
                          <Input
                            type="number" step="0.01" min="0"
                            value={editingProduct.shippingUK ?? editingProduct.shippingDomestic ?? '3.50'}
                            onChange={e => setEditingProduct(p => ({ ...p, shippingUK: e.target.value }))}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Europe (£)</Label>
                          <Input
                            type="number" step="0.01" min="0"
                            value={editingProduct.shippingEurope ?? editingProduct.shippingInternational ?? '8.00'}
                            onChange={e => setEditingProduct(p => ({ ...p, shippingEurope: e.target.value }))}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">USA/Canada (£)</Label>
                          <Input
                            type="number" step="0.01" min="0"
                            value={editingProduct.shippingUSCanada ?? '12.00'}
                            onChange={e => setEditingProduct(p => ({ ...p, shippingUSCanada: e.target.value }))}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Rest of World (£)</Label>
                          <Input
                            type="number" step="0.01" min="0"
                            value={editingProduct.shippingRestOfWorld ?? '14.00'}
                            onChange={e => setEditingProduct(p => ({ ...p, shippingRestOfWorld: e.target.value }))}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <Label className="text-xs">Availability</Label>
                        <select
                          value={editingProduct.availability || 'in_stock'}
                          onChange={e => {
                            const val = e.target.value;
                            setEditingProduct(p => ({
                              ...p,
                              availability: val,
                              ...(val === 'sold_out' ? { stockQuantity: 0 } : {}),
                            }));
                          }}
                          className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                        >
                          <option value="in_stock">In Stock</option>
                          <option value="available_soon">Available Soon</option>
                          <option value="sold_out">Sold Out</option>
                        </select>
                      </div>
                      <div className="flex items-end gap-4 pb-1">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={editingProduct.available}
                            onChange={e => setEditingProduct(p => ({ ...p, available: e.target.checked }))}
                            className="h-4 w-4 rounded border-slate-300 text-teal-600"
                          />
                          <Label className="text-xs">Available for purchase</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={editingProduct.guestAccess ?? false}
                            onChange={e => setEditingProduct(p => ({ ...p, guestAccess: e.target.checked }))}
                            className="h-4 w-4 rounded border-slate-300 text-teal-600"
                          />
                          <Label className="text-xs">Guest access</Label>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveEditProduct} disabled={savingProduct}>
                        <Save className="mr-1 h-3 w-3" />
                        Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingProduct(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  /* Product display row */
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Package className="h-5 w-5 text-slate-400" />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">{product.name}</p>
                          {product.availability && (
                            <Badge className={
                              product.availability === 'in_stock' ? 'bg-green-100 text-green-700' :
                              product.availability === 'available_soon' ? 'bg-blue-100 text-blue-700' :
                              'bg-red-100 text-red-700'
                            }>
                              {product.availability === 'in_stock' ? 'In Stock' :
                               product.availability === 'available_soon' ? 'Available Soon' : 'Sold Out'}
                            </Badge>
                          )}
                          {product.stockQuantity != null && product.stockQuantity !== "" && (
                            Number(product.stockQuantity) <= 0 ? (
                              <Badge className="bg-red-100 text-red-700">Sold Out</Badge>
                            ) : (
                              <span className="text-xs font-medium text-slate-500">
                                {product.stockQuantity} in stock
                              </span>
                            )
                          )}
                          {product.guestAccess && (
                            <Badge className="bg-purple-100 text-purple-700 text-[10px]">Guest</Badge>
                          )}
                        </div>
                        {product.description && (
                          <p className="text-xs text-slate-500">{product.description}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-0.5">
                          {product.productType} · UK £{(product.shippingUK || product.shippingDomestic || 0).toFixed(2)} / EU £{(product.shippingEurope || product.shippingInternational || 0).toFixed(2)} / US/CA £{(product.shippingUSCanada || 0).toFixed(2)} / RoW £{(product.shippingRestOfWorld || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-teal-600">£{(product.price || 0).toFixed(2)}</span>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => toggleAvailability(product)}
                        className={product.available ? 'text-green-600' : 'text-slate-400'}
                        title={product.available ? 'Available — click to hide' : 'Hidden — click to make available'}
                      >
                        {product.available ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingProduct({ ...product })}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteProduct(product)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
        {/* eBooks for selected book */}
        {selectedBookId && ebooks.filter(eb => eb.bookId === selectedBookId).length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-slate-500">eBooks</h3>
            {ebooks.filter(eb => eb.bookId === selectedBookId).map(eb => (
              <Card key={eb.id} className="p-4">
                {editingEbook?.id === eb.id ? (
                  <div className="space-y-3">
                    <p className="font-semibold text-slate-900">{eb.title}</p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <Label className="text-xs">Price (£)</Label>
                        <Input type="number" step="0.01" min="0" value={editingEbook.price}
                          onChange={e => setEditingEbook(p => ({ ...p, price: e.target.value }))} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">Status</Label>
                        <select value={editingEbook.status || 'draft'}
                          onChange={e => setEditingEbook(p => ({ ...p, status: e.target.value }))}
                          className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                          <option value="draft">Draft</option>
                          <option value="scheduled">Scheduled</option>
                          <option value="live">Live</option>
                          <option value="hidden">Hidden</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={editingEbook.visibleToGuests ?? true}
                          onChange={e => {
                            setEditingEbook(p => ({ ...p, visibleToGuests: e.target.checked, ...(e.target.checked ? {} : { availableToGuests: false }) }));
                          }} className="h-4 w-4 rounded border-slate-300 text-teal-600" />
                        Visible to guests
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={editingEbook.availableToGuests ?? false}
                          onChange={e => {
                            setEditingEbook(p => ({ ...p, availableToGuests: e.target.checked, ...(e.target.checked ? { visibleToGuests: true } : {}) }));
                          }} className="h-4 w-4 rounded border-slate-300 text-teal-600" disabled={!(editingEbook.visibleToGuests ?? true)} />
                        Available to guests
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => saveDigitalProduct('ebook', editingEbook)} disabled={savingDigital}>
                        <Save className="mr-1 h-3 w-3" /> Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingEbook(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
                        <FileText className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">{eb.title}</p>
                          <Badge className={eb.status === 'live' ? 'bg-green-100 text-green-700' : eb.status === 'scheduled' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}>
                            {eb.status || 'draft'}
                          </Badge>
                          {eb.visibleToGuests && <Badge className="bg-purple-100 text-purple-700 text-[10px]">Guest visible</Badge>}
                          {eb.availableToGuests && <Badge className="bg-teal-100 text-teal-700 text-[10px]">Guest purchasable</Badge>}
                        </div>
                        <p className="text-xs text-slate-400">eBook{eb.preOrder?.preOrderEnabled ? ' · Pre-order enabled' : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-teal-600">£{(eb.price || 0).toFixed(2)}</span>
                      <Button size="sm" variant="ghost" onClick={() => setEditingEbook({ ...eb })}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Audiobooks for selected book */}
        {selectedBookId && audiobooks.filter(ab => ab.bookId === selectedBookId).length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-slate-500">Audiobooks</h3>
            {audiobooks.filter(ab => ab.bookId === selectedBookId).map(ab => (
              <Card key={ab.id} className="p-4">
                {editingAudiobook?.id === ab.id ? (
                  <div className="space-y-3">
                    <p className="font-semibold text-slate-900">{ab.title}</p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <Label className="text-xs">Price (£)</Label>
                        <Input type="number" step="0.01" min="0" value={editingAudiobook.price}
                          onChange={e => setEditingAudiobook(p => ({ ...p, price: e.target.value }))} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">Status</Label>
                        <select value={editingAudiobook.status || 'draft'}
                          onChange={e => setEditingAudiobook(p => ({ ...p, status: e.target.value }))}
                          className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                          <option value="draft">Draft</option>
                          <option value="scheduled">Scheduled</option>
                          <option value="live">Live</option>
                          <option value="hidden">Hidden</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={editingAudiobook.visibleToGuests ?? true}
                          onChange={e => {
                            setEditingAudiobook(p => ({ ...p, visibleToGuests: e.target.checked, ...(e.target.checked ? {} : { availableToGuests: false }) }));
                          }} className="h-4 w-4 rounded border-slate-300 text-teal-600" />
                        Visible to guests
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={editingAudiobook.availableToGuests ?? false}
                          onChange={e => {
                            setEditingAudiobook(p => ({ ...p, availableToGuests: e.target.checked, ...(e.target.checked ? { visibleToGuests: true } : {}) }));
                          }} className="h-4 w-4 rounded border-slate-300 text-teal-600" disabled={!(editingAudiobook.visibleToGuests ?? true)} />
                        Available to guests
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => saveDigitalProduct('audiobook', editingAudiobook)} disabled={savingDigital}>
                        <Save className="mr-1 h-3 w-3" /> Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingAudiobook(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-50">
                        <Headphones className="h-4 w-4 text-purple-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">{ab.title}</p>
                          <Badge className={ab.status === 'live' ? 'bg-green-100 text-green-700' : ab.status === 'scheduled' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}>
                            {ab.status || 'draft'}
                          </Badge>
                          {ab.visibleToGuests && <Badge className="bg-purple-100 text-purple-700 text-[10px]">Guest visible</Badge>}
                          {ab.availableToGuests && <Badge className="bg-teal-100 text-teal-700 text-[10px]">Guest purchasable</Badge>}
                        </div>
                        <p className="text-xs text-slate-400">Audiobook · {ab.chapterCount || 0} chapters{ab.preOrder?.preOrderEnabled ? ' · Pre-order enabled' : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-teal-600">£{(ab.price || 0).toFixed(2)}</span>
                      <Button size="sm" variant="ghost" onClick={() => setEditingAudiobook({ ...ab })}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════
          SECTION A2: PROMO CODES
          ═══════════════════════════════════════ */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Tag className="h-5 w-5 text-teal-600" /> Promo Codes
            </h2>
            <p className="text-sm text-slate-500">Create discount codes for your readers</p>
          </div>
          <Button onClick={() => setShowPromoForm(!showPromoForm)} size="sm">
            <Plus className="mr-1 h-4 w-4" />
            New Code
          </Button>
        </div>

        {/* Add / Edit promo code form */}
        {(showPromoForm || editingPromo) && (() => {
          const isEditing = !!editingPromo;
          const promo = isEditing ? editingPromo : newPromo;
          const setPromo = isEditing ? setEditingPromo : setNewPromo;
          // Normalise legacy data: old codes used discountMode='percentage' to mean "all products, percentage"
          const mode = promo.discountMode === 'percentage' ? 'all' : (promo.discountMode || 'all');
          const dType = promo.discountType || 'percentage';
          const overrides = promo.productOverrides || {};
          const ppd = promo.perProductDiscounts || {};

          const setOverrideField = (productId, field, value) => {
            setPromo(p => ({
              ...p,
              productOverrides: {
                ...p.productOverrides,
                [productId]: { ...(p.productOverrides?.[productId] || {}), [field]: value },
              },
            }));
          };

          const toggleProductOverride = (product) => {
            setPromo(p => {
              const current = { ...(p.productOverrides || {}) };
              if (current[product.id]) {
                delete current[product.id];
              } else {
                current[product.id] = true;
              }
              return { ...p, productOverrides: current };
            });
          };

          const hasCategoryDiscounts = Object.values(ppd).some(v => v !== '' && v !== undefined);
          const isFreeShipping = dType === 'free_shipping';
          const canSave = promo.code && (
            isFreeShipping
              ? true
              : mode === 'per_product'
                ? (hasCategoryDiscounts || Object.keys(overrides).length > 0)
                : !!promo.discountValue
          );

          return (
            <Card className="border-teal-200 bg-teal-50/30">
              <CardContent className="p-5 space-y-4">
                <h3 className="font-semibold text-slate-900">{isEditing ? 'Edit Promo Code' : 'New Promo Code'}</h3>

                {/* Row 1: Code + Active toggle */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label>Code *</Label>
                    <Input
                      value={promo.code}
                      onChange={e => setPromo(p => ({ ...p, code: e.target.value.toUpperCase().replace(/\s/g, '') }))}
                      placeholder="e.g. LAUNCH20"
                      className="mt-1 font-mono"
                    />
                  </div>
                  <div className="flex items-end gap-4">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={promo.active !== false}
                        onChange={e => setPromo(p => ({ ...p, active: e.target.checked }))}
                        className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                      Active
                    </label>
                  </div>
                </div>

                {/* Row 2: Discount type + Discount mode */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label>Discount Type</Label>
                    <select
                      value={dType}
                      onChange={e => setPromo(p => ({ ...p, discountType: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount (£)</option>
                      <option value="free_shipping">Free Shipping</option>
                    </select>
                  </div>
                  <div className={isFreeShipping ? 'opacity-50 pointer-events-none' : ''}>
                    <Label>Applies To</Label>
                    <select
                      value={mode}
                      disabled={isFreeShipping}
                      onChange={e => setPromo(p => ({ ...p, discountMode: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                    >
                      <option value="all">All products (one discount)</option>
                      <option value="per_product">Per product type (different discounts)</option>
                    </select>
                  </div>
                </div>

                {isFreeShipping && (
                  <div className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-sm text-teal-800">
                    🎉 This code will waive shipping costs on physical orders. No discount value needed.
                  </div>
                )}

                {/* Blanket discount field (discountMode = 'all') */}
                {!isFreeShipping && mode === 'all' && (
                  <div className="max-w-xs">
                    <Label>Discount Value *</Label>
                    <div className="flex items-center gap-2 mt-1">
                      {dType === 'fixed' && <span className="text-sm font-medium text-slate-500">£</span>}
                      <Input
                        type="number" step={dType === 'percentage' ? '1' : '0.01'} min="0" max={dType === 'percentage' ? '100' : undefined}
                        value={promo.discountValue}
                        onChange={e => setPromo(p => ({ ...p, discountValue: e.target.value }))}
                        placeholder={dType === 'percentage' ? '20' : '5.00'}
                      />
                      {dType === 'percentage' && <span className="text-sm font-medium text-slate-500">%</span>}
                    </div>
                  </div>
                )}

                {/* Per-product-type category discounts (discountMode = 'per_product') */}
                {!isFreeShipping && mode === 'per_product' && (
                  <div className="space-y-4">
                    <div>
                      <Label className="mb-2 block">Category Discounts</Label>
                      <p className="text-xs text-slate-500 mb-3">Set a {dType === 'percentage' ? 'percentage' : 'fixed amount'} discount per product type. Leave blank to skip that type.</p>
                      <div className="grid gap-3 sm:grid-cols-3">
                        <div>
                          <Label className="text-xs text-slate-500 flex items-center gap-1.5"><BookText className="h-3.5 w-3.5" /> eBooks</Label>
                          <div className="flex items-center gap-1.5 mt-1">
                            {dType === 'fixed' && <span className="text-xs text-slate-400">£</span>}
                            <Input type="number" step={dType === 'percentage' ? '1' : '0.01'} min="0" max={dType === 'percentage' ? '100' : undefined}
                              value={ppd.ebook ?? ''}
                              onChange={e => setPromo(p => ({ ...p, perProductDiscounts: { ...p.perProductDiscounts, ebook: e.target.value } }))}
                              placeholder={dType === 'percentage' ? '10' : '2.00'}
                              className="text-sm h-8" />
                            {dType === 'percentage' && <span className="text-xs text-slate-400">%</span>}
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500 flex items-center gap-1.5"><Headphones className="h-3.5 w-3.5" /> Audiobooks</Label>
                          <div className="flex items-center gap-1.5 mt-1">
                            {dType === 'fixed' && <span className="text-xs text-slate-400">£</span>}
                            <Input type="number" step={dType === 'percentage' ? '1' : '0.01'} min="0" max={dType === 'percentage' ? '100' : undefined}
                              value={ppd.audiobook ?? ''}
                              onChange={e => setPromo(p => ({ ...p, perProductDiscounts: { ...p.perProductDiscounts, audiobook: e.target.value } }))}
                              placeholder={dType === 'percentage' ? '15' : '3.00'}
                              className="text-sm h-8" />
                            {dType === 'percentage' && <span className="text-xs text-slate-400">%</span>}
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500 flex items-center gap-1.5"><Package className="h-3.5 w-3.5" /> Physical Products</Label>
                          <div className="flex items-center gap-1.5 mt-1">
                            {dType === 'fixed' && <span className="text-xs text-slate-400">£</span>}
                            <Input type="number" step={dType === 'percentage' ? '1' : '0.01'} min="0" max={dType === 'percentage' ? '100' : undefined}
                              value={ppd.physical ?? ''}
                              onChange={e => setPromo(p => ({ ...p, perProductDiscounts: { ...p.perProductDiscounts, physical: e.target.value } }))}
                              placeholder={dType === 'percentage' ? '20' : '5.00'}
                              className="text-sm h-8" />
                            {dType === 'percentage' && <span className="text-xs text-slate-400">%</span>}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Legacy per-product-ID overrides (physical products only) */}
                    {allProducts.length > 0 && (
                      <div>
                        <Label className="mb-2 block">Advanced: Per-Product Overrides <span className="text-xs text-slate-400 font-normal">(optional — overrides category discounts for specific physical products)</span></Label>
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                          {books.filter(b => b.status !== 'archived').map(book => {
                            const bookProds = allProducts.filter(p => p.bookId === book.id);
                            if (!bookProds.length) return null;
                            return (
                              <div key={book.id} className="rounded-lg border border-slate-200 bg-white">
                                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                                  <p className="text-sm font-semibold text-slate-700">{book.title}</p>
                                </div>
                                <div className="divide-y divide-slate-100">
                                  {bookProds.map(product => {
                                    const isEnabled = !!overrides[product.id];
                                    const ov = overrides[product.id] || {};
                                    return (
                                      <div key={product.id} className={`p-3 transition-colors ${isEnabled ? 'bg-teal-50/30' : ''}`}>
                                        <div className="flex items-center gap-3 mb-2">
                                          <input type="checkbox" checked={isEnabled} onChange={() => toggleProductOverride(product)}
                                            className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                                          <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-slate-900">{product.name}</p>
                                            <p className="text-xs text-slate-400">
                                              Current: £{Number(product.price || 0).toFixed(2)} + shipping UK £{Number(product.shippingUK ?? 3.50).toFixed(2)} / EU £{Number(product.shippingEurope ?? 8.00).toFixed(2)}
                                            </p>
                                          </div>
                                        </div>
                                        {isEnabled && (
                                          <p className="ml-7 text-xs text-teal-600">Category discount will apply to this product</p>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Row 3: Expiry + Max Uses */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label>Expiry Date <span className="text-xs text-slate-400 font-normal">(optional)</span></Label>
                    <Input type="date"
                      value={promo.expiresAt || ''}
                      onChange={e => setPromo(p => ({ ...p, expiresAt: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Max Uses <span className="text-xs text-slate-400 font-normal">(optional — blank = unlimited)</span></Label>
                    <Input type="number" min="1" step="1"
                      value={promo.maxUses || ''}
                      onChange={e => setPromo(p => ({ ...p, maxUses: e.target.value }))}
                      placeholder="Unlimited"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button onClick={isEditing ? saveEditPromo : addPromoCode} disabled={savingPromo || !canSave} size="sm">
                    <Save className="mr-1 h-4 w-4" />
                    {savingPromo ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Code'}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => {
                    if (isEditing) setEditingPromo(null);
                    else { setShowPromoForm(false); setNewPromo({ ...defaultPromo }); }
                  }}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Promo codes list */}
        {loadingPromos ? (
          <p className="text-sm text-slate-400">Loading promo codes...</p>
        ) : promoCodes.length === 0 ? (
          <Card className="p-6 text-center">
            <Tag className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-2 text-sm text-slate-500">No promo codes yet. Create one to offer discounts to your readers.</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {promoCodes.map(promo => {
              // Normalise legacy: old codes used discountMode='percentage' for "all products percentage"
              const promoMode = promo.discountMode === 'percentage' ? 'all' : (promo.discountMode || 'all');
              const dType = promo.discountType || 'percentage';
              const overrideCount = Object.keys(promo.productOverrides || {}).length;
              const ppd = promo.perProductDiscounts || {};
              const hasCategoryDiscounts = Object.keys(ppd).length > 0;
              const isExpired = promo.expiresAt && new Date(promo.expiresAt) < new Date();
              const isMaxedOut = promo.maxUses && (promo.usageCount || 0) >= promo.maxUses;

              // Build discount summary
              let discountSummary;
              if (dType === 'free_shipping' || promo.freeShipping) {
                discountSummary = 'Free shipping';
              } else if (promoMode === 'all') {
                discountSummary = dType === 'percentage'
                  ? `${promo.discountValue}% off everything`
                  : `£${Number(promo.discountValue || 0).toFixed(2)} off everything`;
              } else if (hasCategoryDiscounts) {
                const parts = [];
                if (ppd.ebook !== undefined) parts.push(`eBook: ${dType === 'percentage' ? ppd.ebook + '%' : '£' + Number(ppd.ebook).toFixed(2)}`);
                if (ppd.audiobook !== undefined) parts.push(`Audio: ${dType === 'percentage' ? ppd.audiobook + '%' : '£' + Number(ppd.audiobook).toFixed(2)}`);
                if (ppd.physical !== undefined) parts.push(`Physical: ${dType === 'percentage' ? ppd.physical + '%' : '£' + Number(ppd.physical).toFixed(2)}`);
                discountSummary = parts.join(' · ');
              } else {
                discountSummary = `Custom pricing · ${overrideCount} product${overrideCount !== 1 ? 's' : ''}`;
              }

              return (
                <Card key={promo.id} className={`transition-colors ${promo.active && !isExpired && !isMaxedOut ? 'border-slate-200' : 'border-slate-100 bg-slate-50/50 opacity-60'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-wrap">
                        <code className="rounded bg-slate-100 px-2.5 py-1 text-sm font-bold text-slate-800">{promo.code}</code>
                        <Badge className={promoMode === 'all' ? 'bg-purple-100 text-purple-700' : 'bg-teal-100 text-teal-700'}>
                          {discountSummary}
                        </Badge>
                        <span className="text-xs text-slate-400">
                          Used {promo.usageCount || 0}{promo.maxUses ? `/${promo.maxUses}` : ''} times
                        </span>
                        {promo.expiresAt && (
                          <span className={`text-xs ${isExpired ? 'text-red-500' : 'text-slate-400'}`}>
                            {isExpired ? 'Expired' : `Expires ${new Date(promo.expiresAt).toLocaleDateString()}`}
                          </span>
                        )}
                        {!promo.active && <Badge className="bg-slate-100 text-slate-500">Inactive</Badge>}
                        {isExpired && promo.active && <Badge className="bg-red-100 text-red-600">Expired</Badge>}
                        {isMaxedOut && promo.active && <Badge className="bg-yellow-100 text-yellow-700">Max uses reached</Badge>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button size="sm" variant="ghost" onClick={() => copyPromoCode(promo)} title="Copy code">
                          {copiedPromoId === promo.id ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => togglePromoActive(promo)} title={promo.active ? 'Deactivate' : 'Activate'}>
                          {promo.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => {
                          setEditingPromo({
                            ...promo,
                            discountMode: promoMode,
                            discountType: dType,
                            perProductDiscounts: ppd,
                            maxUses: promo.maxUses || '',
                            expiresAt: promo.expiresAt || '',
                          });
                          setShowPromoForm(false);
                        }} title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deletePromoCode(promo)} title="Delete">
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════
          SECTION B: ORDERS LIST
          ═══════════════════════════════════════ */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">Orders</h2>

        {/* Stats row */}
        <div className="grid gap-3 sm:grid-cols-5">
          <Card className="p-3">
            <p className="text-xs text-slate-500">Total Orders</p>
            <p className="text-xl font-bold text-slate-900">{stats.total}</p>
          </Card>
          <Card className="p-3 border-green-100 bg-green-50">
            <p className="text-xs text-green-700">Paid</p>
            <p className="text-xl font-bold text-green-900">{stats.paid}</p>
          </Card>
          <Card className="p-3 border-blue-100 bg-blue-50">
            <p className="text-xs text-blue-700">Shipped</p>
            <p className="text-xl font-bold text-blue-900">{stats.shipped}</p>
          </Card>
          <Card className="p-3 border-green-100 bg-green-50">
            <p className="text-xs text-green-700">Revenue</p>
            <p className="text-xl font-bold text-green-900">£{stats.revenue.toFixed(2)}</p>
          </Card>
          <Card className="p-3 border-indigo-100 bg-indigo-50">
            <p className="text-xs text-indigo-700">Pre-Orders</p>
            <p className="text-xl font-bold text-indigo-900">{stats.preOrders}</p>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search orders..."
              value={orderSearch}
              onChange={e => setOrderSearch(e.target.value)}
              className="w-56 pl-9"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setOrderBookFilter('all')}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                orderBookFilter === 'all'
                  ? 'border-teal-300 bg-teal-50 text-teal-800'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
            >
              All Books
            </button>
            {activeBooks.map(b => (
              <button
                key={b.id}
                onClick={() => setOrderBookFilter(b.id)}
                className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                  orderBookFilter === b.id
                    ? 'border-teal-300 bg-teal-50 text-teal-800'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                {b.title}
              </button>
            ))}
          </div>
          <select
            value={orderStatusFilter}
            onChange={e => setOrderStatusFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-500"
          >
            <option value="all">All Statuses</option>
            {ORDER_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>

        {/* Orders list — grouped by Book → Product → UK/International */}
        {loadingOrders ? (
          <div className="py-8 text-center text-sm text-slate-400">Loading orders...</div>
        ) : regularOrders.length === 0 && preOrders.length === 0 ? (
          <Card className="p-8 text-center">
            <ShoppingCart className="mx-auto mb-2 h-10 w-10 text-slate-300" />
            <p className="text-slate-500">
              {orders.length === 0 ? 'No orders yet' : 'No orders match your filters'}
            </p>
          </Card>
        ) : (() => {
          // Group: Book → Product → UK/International
          const bookGroups = {};
          regularOrders.forEach(order => {
            const bookTitle = books.find(b => b.id === order.bookId)?.title || order.bookTitle || 'Unknown Book';
            const productName = order.productName || 'Unknown Product';
            const region = isUK(order.shippingAddress?.country) ? 'uk' : 'international';
            if (!bookGroups[bookTitle]) bookGroups[bookTitle] = {};
            if (!bookGroups[bookTitle][productName]) bookGroups[bookTitle][productName] = { uk: [], international: [] };
            bookGroups[bookTitle][productName][region].push(order);
          });

          const PAID = ['Paid', 'Shipped', 'Delivered', 'Completed'];

          return (
            <div className="space-y-5">
              {Object.entries(bookGroups).map(([bookTitle, productGroups]) => {
                const allBookOrders = Object.values(productGroups).flatMap(p => [...p.uk, ...p.international]);
                const bookTotal = allBookOrders.reduce((s, o) => s + (o.amount || o.total || 0), 0);
                const bookPaid = allBookOrders.filter(o => PAID.includes(normalizeStatus(o.status))).reduce((s, o) => s + (o.amount || o.total || 0), 0);
                const isBookCollapsed = collapsedBooks[bookTitle];

                return (
                  <div key={bookTitle} className="rounded-2xl border-2 border-slate-200 overflow-hidden shadow-sm">
                    {/* Book header */}
                    <Button
                      variant="ghost"
                      onClick={() => setCollapsedBooks(prev => ({ ...prev, [bookTitle]: !prev[bookTitle] }))}
                      className="h-auto w-full rounded-none flex items-center justify-between px-5 py-4 bg-gradient-to-r from-slate-50 to-slate-100/60 hover:from-slate-100 hover:to-slate-100 text-left"
                    >
                      <div className="flex items-center gap-3">
                        <BookOpen className="h-5 w-5 text-teal-600 shrink-0" />
                        <div>
                          <p className="font-bold text-slate-900 text-base">{bookTitle}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{allBookOrders.length} order{allBookOrders.length !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right hidden sm:block">
                          <p className="text-xs text-slate-500">Revenue / Total</p>
                          <p className="text-sm font-bold text-teal-700">£{bookPaid.toFixed(2)} <span className="font-normal text-slate-400">/ £{bookTotal.toFixed(2)}</span></p>
                        </div>
                        {isBookCollapsed ? <ChevronDown className="h-5 w-5 text-teal-600" /> : <ChevronUp className="h-5 w-5 text-teal-600" />}
                      </div>
                    </Button>

                    {!isBookCollapsed && (
                      <div className="divide-y divide-slate-100">
                        {Object.entries(productGroups).map(([productName, { uk, international }]) => {
                          const allProd = [...uk, ...international];
                          const prodTotal = allProd.reduce((s, o) => s + (o.amount || o.total || 0), 0);
                          const prodKey = `${bookTitle}__${productName}`;
                          const isProdCollapsed = collapsedProducts[prodKey];

                          return (
                            <div key={prodKey}>
                              {/* Product sub-header */}
                              <Button
                                variant="ghost"
                                onClick={() => setCollapsedProducts(prev => ({ ...prev, [prodKey]: !prev[prodKey] }))}
                                className="h-auto w-full rounded-none flex items-center justify-between px-5 py-3 bg-white hover:bg-slate-50 text-left border-l-4 border-teal-300"
                              >
                                <div className="flex items-center gap-2">
                                  <Package className="h-4 w-4 text-slate-500 shrink-0" />
                                  <span className="text-sm font-semibold text-slate-800">{productName}</span>
                                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600">{allProd.length}</span>
                                  {uk.length > 0 && <span className="text-xs text-slate-400">{uk.length} UK</span>}
                                  {international.length > 0 && <span className="text-xs text-slate-400">{international.length} Intl</span>}
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-xs font-semibold text-slate-500 hidden sm:block">£{prodTotal.toFixed(2)}</span>
                                  {isProdCollapsed ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronUp className="h-4 w-4 text-slate-400" />}
                                </div>
                              </Button>

                              {!isProdCollapsed && (
                                <div>
                                  {[
                                    { key: 'uk', label: '🇬🇧 UK', orders: uk, accent: 'bg-blue-50 border-blue-100 text-blue-800' },
                                    { key: 'international', label: '🌍 International', orders: international, accent: 'bg-violet-50 border-violet-100 text-violet-800' },
                                  ].map(({ key, label, orders: regionOrders, accent }) => {
                                    if (regionOrders.length === 0) return null;
                                    const regionKey = `${prodKey}__${key}`;
                                    const isRegionCollapsed = collapsedRegions[regionKey];
                                    const regionTotal = regionOrders.reduce((s, o) => s + (o.amount || o.total || 0), 0);

                                    return (
                                      <div key={key}>
                                        <Button
                                          variant="ghost"
                                          onClick={() => setCollapsedRegions(prev => ({ ...prev, [regionKey]: !prev[regionKey] }))}
                                          className={`h-auto w-full rounded-none flex items-center justify-between px-5 py-2.5 border-l-4 ${accent} hover:opacity-80 text-left`}
                                        >
                                          <div className="flex items-center gap-2">
                                            <MapPin className="h-3.5 w-3.5 shrink-0 opacity-70" />
                                            <span className="text-xs font-semibold">{label}</span>
                                            <span className="rounded-full bg-white/70 px-2 py-0.5 text-xs font-bold">{regionOrders.length}</span>
                                          </div>
                                          <div className="flex items-center gap-3">
                                            <span className="text-xs font-semibold hidden sm:block">£{regionTotal.toFixed(2)}</span>
                                            {isRegionCollapsed ? <ChevronDown className="h-3.5 w-3.5 opacity-70" /> : <ChevronUp className="h-3.5 w-3.5 opacity-70" />}
                                          </div>
                                        </Button>

                                        {!isRegionCollapsed && (
                                          <div className="space-y-2 p-4 bg-white">
                                            {regionOrders.map(order => {
                                              const isExpanded = expandedOrder === order.id;
                                              const status = normalizeStatus(order.status);
                                              const statusInfo = ORDER_STATUSES.find(s => s.key === status) || (status === PRE_ORDER_STATUS.key ? PRE_ORDER_STATUS : ORDER_STATUSES[0]);
                                              const StatusIcon = statusInfo.icon;

                                              return (
                                                <Card key={order.id} className="overflow-hidden">
                                                  <div
                                                    className="flex cursor-pointer items-center justify-between px-4 py-3 hover:bg-slate-50"
                                                    onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                                                  >
                                                    <div className="flex items-center gap-3">
                                                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-teal-400 to-teal-600 text-xs font-bold text-white shrink-0">
                                                        {order.readerName?.[0]?.toUpperCase() || 'R'}
                                                      </div>
                                                      <div>
                                                        <div className="flex items-center gap-1.5">
                                                          <p className="font-semibold text-slate-900">{order.readerName || order.readerEmail}</p>
                                                          {order.type === 'pre-order' && (
                                                            <Badge className={`text-[10px] ${order.productType === 'ebook' ? 'bg-blue-100 text-blue-700' : order.productType === 'audiobook' ? 'bg-purple-100 text-purple-700' : 'bg-indigo-100 text-indigo-700'}`}>
                                                              {order.productType === 'ebook' ? 'eBook' : order.productType === 'audiobook' ? 'Audiobook' : ''} Pre-Order
                                                            </Badge>
                                                          )}
                                                          <Badge className="text-[10px] bg-slate-100 text-slate-500">
                                                            {order.source === 'guest_storefront' ? 'Store' : order.readerId && !order.source ? 'Portal Reader' : 'Guest'}
                                                          </Badge>
                                                        </div>
                                                        <p className="text-xs text-slate-500">
                                                          {order.readerEmail && order.readerEmail !== order.readerName ? <span className="mr-2">{order.readerEmail}</span> : null}
                                                          {order.createdAt?.toDate ? order.createdAt.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                                                          {order.type === 'pre-order' && order.deliveryMethod ? ` · ${order.deliveryMethod === 'device' ? `Kindle: ${order.deviceEmail}` : 'Read online'}` : ''}
                                                        </p>
                                                      </div>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                      <span className="font-bold text-teal-600">£{(order.amount || order.total || 0).toFixed(2)}</span>
                                                      <Badge className={statusInfo.color}>
                                                        <StatusIcon className="mr-1 h-3 w-3" />{statusInfo.label}
                                                      </Badge>
                                                      {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                                                    </div>
                                                  </div>

                                                  {isExpanded && (
                                                    <div className="border-t border-slate-100 bg-slate-50/50 p-5 space-y-4">
                                                      <div className="grid gap-4 md:grid-cols-2">
                                                        <div className="space-y-3">
                                                          <h4 className="text-sm font-semibold text-slate-700">Order Details</h4>
                                                          <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2 text-sm">
                                                            <div className="flex justify-between">
                                                              <span className="text-slate-600">Price</span>
                                                              <span className="font-semibold">£{(order.productPrice || order.amount || 0).toFixed(2)}</span>
                                                            </div>
                                                            {order.shippingCost != null && (
                                                              <div className="flex justify-between">
                                                                <span className="text-slate-600">Shipping</span>
                                                                <span className="font-semibold">£{(order.shippingCost || 0).toFixed(2)}</span>
                                                              </div>
                                                            )}
                                                            {order.promoCode && (
                                                              <div className="flex justify-between text-teal-600">
                                                                <span className="flex items-center gap-1"><Tag className="h-3 w-3" /> {order.promoCode}</span>
                                                                <span>−£{(order.discountAmount || 0).toFixed(2)}</span>
                                                              </div>
                                                            )}
                                                            <div className="flex justify-between border-t pt-2 font-bold">
                                                              <span>Total</span>
                                                              <span className="text-teal-600">£{(order.amount || order.total || 0).toFixed(2)}</span>
                                                            </div>
                                                            {order.stripeSessionId && (
                                                              <p className="text-xs text-slate-400 mt-1">Stripe: {order.stripeSessionId}</p>
                                                            )}
                                                          </div>
                                                        </div>
                                                        <div className="space-y-3">
                                                          <h4 className="text-sm font-semibold text-slate-700">Reader Details</h4>
                                                          <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2 text-sm">
                                                            <div className="flex items-center gap-2"><User className="h-4 w-4 text-slate-400" /><span>{order.readerName || 'N/A'}</span></div>
                                                            <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /><span className="break-all">{order.readerEmail}</span></div>
                                                            {(() => { const rdr = readers.find(r => r.emailCorrespondence === order.readerEmail); return rdr?.phoneNumber ? <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /><span>{rdr.phoneNumber}</span></div> : null; })()}
                                                            {order.type === 'pre-order' && (
                                                              <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-indigo-400" /><span>Pre-order · {order.productType === 'ebook' ? 'eBook' : order.productType === 'audiobook' ? 'Audiobook' : order.productType} · {order.deliveryMethod === 'device' ? `Send to ${order.deviceEmail}` : 'Read online'}</span></div>
                                                            )}
                                                            {order.shippingAddress && (
                                                              <div className="flex items-start gap-2">
                                                                <MapPin className="h-4 w-4 text-slate-400 mt-0.5" />
                                                                <div>
                                                                  {order.shippingAddress.line1 && <p>{order.shippingAddress.line1}</p>}
                                                                  {order.shippingAddress.line2 && <p>{order.shippingAddress.line2}</p>}
                                                                  {order.shippingAddress.city && <p>{order.shippingAddress.city}{order.shippingAddress.postalCode && `, ${order.shippingAddress.postalCode}`}</p>}
                                                                  {order.shippingAddress.country && <p className="font-semibold">{order.shippingAddress.country}</p>}
                                                                </div>
                                                              </div>
                                                            )}
                                                          </div>
                                                        </div>
                                                      </div>
                                                      <div className="border-t border-slate-200 pt-4">
                                                        <h4 className="mb-2 text-sm font-semibold text-slate-700">Update Status</h4>
                                                        <div className="flex flex-wrap gap-2">
                                                          {ORDER_STATUSES.map(s => (
                                                            <Button key={s.key} size="sm" variant="secondary" disabled={status === s.key}
                                                              onClick={() => updateOrderStatus(order.id, s.key)}
                                                              className={`text-xs ${status === s.key ? 'opacity-50' : ''}`}
                                                            >{s.label}</Button>
                                                          ))}
                                                        </div>
                                                      </div>

                                                      {/* Tracking info */}
                                                      {order.type !== 'pre-order' && (
                                                        <div className="border-t border-slate-200 pt-4">
                                                          <h4 className="mb-2 text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                                                            <Truck className="h-4 w-4 text-slate-400" /> Tracking Information
                                                          </h4>
                                                          <div className="grid gap-3 sm:grid-cols-3 items-end">
                                                            <div>
                                                              <Label className="mb-1 block text-xs text-slate-500">Delivery Provider</Label>
                                                              <Input
                                                                type="text"
                                                                placeholder="e.g. Royal Mail, Evri, DPD"
                                                                value={getTrackingEdit(order).deliveryProvider}
                                                                onChange={e => setTrackingField(order.id, 'deliveryProvider', e.target.value)}
                                                                className="text-sm"
                                                              />
                                                            </div>
                                                            <div>
                                                              <Label className="mb-1 block text-xs text-slate-500">Tracking Number</Label>
                                                              <Input
                                                                type="text"
                                                                placeholder="Enter tracking number"
                                                                value={getTrackingEdit(order).trackingNumber}
                                                                onChange={e => setTrackingField(order.id, 'trackingNumber', e.target.value)}
                                                                className="text-sm"
                                                              />
                                                            </div>
                                                            <Button
                                                              size="sm"
                                                              onClick={() => saveTracking(order)}
                                                              disabled={trackingSaving[order.id]}
                                                              className="bg-teal-600 text-white hover:bg-teal-700"
                                                            >
                                                              <Save className="mr-1.5 h-3.5 w-3.5" />
                                                              {trackingSaving[order.id] ? 'Saving...' : 'Save Tracking'}
                                                            </Button>
                                                          </div>
                                                        </div>
                                                      )}
                                                    </div>
                                                  )}
                                                </Card>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* ── PRE-ORDERS SECTION ── */}
              {preOrders.length > 0 && (() => {
                const ebookPreOrders = preOrders.filter(o => o.productType === 'ebook');
                const audiobookPreOrders = preOrders.filter(o => o.productType === 'audiobook');
                const otherPreOrders = preOrders.filter(o => o.productType !== 'ebook' && o.productType !== 'audiobook');

                const renderPreOrderCard = (order) => {
                  const isExpanded = expandedOrder === order.id;
                  const status = normalizeStatus(order.status);
                  const statusInfo = ORDER_STATUSES.find(s => s.key === status) || (status === PRE_ORDER_STATUS.key ? PRE_ORDER_STATUS : ORDER_STATUSES[0]);
                  const StatusIcon = statusInfo.icon;
                  return (
                    <Card key={order.id} className="overflow-hidden border-indigo-100">
                      <div className="flex cursor-pointer items-center justify-between px-4 py-3 hover:bg-indigo-50/50"
                        onClick={() => setExpandedOrder(isExpanded ? null : order.id)}>
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 text-xs font-bold text-white shrink-0">
                            {order.readerName?.[0]?.toUpperCase() || 'G'}
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="font-semibold text-slate-900">{order.readerName || order.readerEmail}</p>
                              <Badge className="text-[10px] bg-slate-100 text-slate-500">
                                {order.source === 'guest_storefront' ? 'Store' : order.readerId ? 'Portal' : 'Guest'}
                              </Badge>
                            </div>
                            <p className="text-xs text-slate-500">
                              {order.readerEmail} · {order.createdAt?.toDate ? order.createdAt.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                              {order.deliveryMethod ? ` · ${order.deliveryMethod === 'device' ? `Kindle: ${order.deviceEmail}` : 'Read online'}` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-indigo-600">£{(order.amount || order.price || 0).toFixed(2)}</span>
                          <Badge className={statusInfo.color}><StatusIcon className="mr-1 h-3 w-3" />{statusInfo.label}</Badge>
                          {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="border-t border-indigo-100 bg-indigo-50/30 p-5 space-y-4">
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-3">
                              <h4 className="text-sm font-semibold text-slate-700">Order Details</h4>
                              <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2 text-sm">
                                <p><strong>Book:</strong> {order.bookTitle}</p>
                                <p><strong>Format:</strong> {order.productType === 'ebook' ? 'eBook' : 'Audiobook'}</p>
                                <p><strong>Price:</strong> £{(order.price || 0).toFixed(2)}</p>
                                <p><strong>Payment:</strong> {order.paymentStatus || 'unpaid'}</p>
                                <p><strong>Delivery:</strong> {order.deliveryMethod === 'device' ? `Send to device (${order.deviceEmail})` : 'Read online'}</p>
                              </div>
                            </div>
                            <div className="space-y-3">
                              <h4 className="text-sm font-semibold text-slate-700">Buyer Details</h4>
                              <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2 text-sm">
                                <div className="flex items-center gap-2"><User className="h-4 w-4 text-slate-400" /><span>{order.readerName || 'N/A'}</span></div>
                                <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /><span className="break-all">{order.readerEmail}</span></div>
                                {(() => { const rdr = readers.find(r => r.emailCorrespondence === order.readerEmail); return rdr?.phoneNumber ? <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /><span>{rdr.phoneNumber}</span></div> : null; })()}
                              </div>
                            </div>
                          </div>
                          <div className="border-t border-slate-200 pt-4">
                            <h4 className="mb-2 text-sm font-semibold text-slate-700">Update Status</h4>
                            <div className="flex flex-wrap gap-2">
                              {ORDER_STATUSES.map(s => (
                                <Button key={s.key} size="sm" variant="secondary" disabled={status === s.key}
                                  onClick={() => updateOrderStatus(order.id, s.key)}
                                  className={`text-xs ${status === s.key ? 'opacity-50' : ''}`}
                                >{s.label}</Button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                };

                return (
                  <div className="mt-8 space-y-3">
                    <h3 className="text-lg font-semibold text-indigo-900 flex items-center gap-2">
                      Pre-Orders <Badge className="bg-indigo-100 text-indigo-700">{preOrders.length}</Badge>
                    </h3>

                    {/* eBook Pre-Orders dropdown */}
                    {ebookPreOrders.length > 0 && (
                      <div className="rounded-xl border border-blue-200 overflow-hidden">
                        <Button variant="ghost" onClick={() => setCollapsedPreOrderType(p => ({ ...p, ebook: !p.ebook }))}
                          className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 hover:bg-blue-100 rounded-none">
                          <span className="flex items-center gap-2 font-semibold text-blue-800">
                            <FileText className="h-4 w-4" /> eBook Pre-Orders
                            <Badge className="bg-blue-100 text-blue-700">{ebookPreOrders.length}</Badge>
                          </span>
                          {collapsedPreOrderType.ebook ? <ChevronDown className="h-4 w-4 text-blue-400" /> : <ChevronUp className="h-4 w-4 text-blue-400" />}
                        </Button>
                        {!collapsedPreOrderType.ebook && (
                          <div className="space-y-2 p-3 bg-white">
                            {ebookPreOrders.map(renderPreOrderCard)}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Audiobook Pre-Orders dropdown */}
                    {audiobookPreOrders.length > 0 && (
                      <div className="rounded-xl border border-purple-200 overflow-hidden">
                        <Button variant="ghost" onClick={() => setCollapsedPreOrderType(p => ({ ...p, audiobook: !p.audiobook }))}
                          className="w-full flex items-center justify-between px-4 py-3 bg-purple-50 hover:bg-purple-100 rounded-none">
                          <span className="flex items-center gap-2 font-semibold text-purple-800">
                            <Headphones className="h-4 w-4" /> Audiobook Pre-Orders
                            <Badge className="bg-purple-100 text-purple-700">{audiobookPreOrders.length}</Badge>
                          </span>
                          {collapsedPreOrderType.audiobook ? <ChevronDown className="h-4 w-4 text-purple-400" /> : <ChevronUp className="h-4 w-4 text-purple-400" />}
                        </Button>
                        {!collapsedPreOrderType.audiobook && (
                          <div className="space-y-2 p-3 bg-white">
                            {audiobookPreOrders.map(renderPreOrderCard)}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Other pre-orders (if any) */}
                    {otherPreOrders.length > 0 && (
                      <div className="space-y-2">{otherPreOrders.map(renderPreOrderCard)}</div>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
